import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { AuthState } from "./auth";

export const validRequestStatus = ["O", "OP", "I"];

export interface ActionableRequestState {
  id: string;
  datetime_created: {
    string: string;
    timestamp: number;
  };
  datetime_updated: string;
  category: [string, string];
  request: string;
  details: string;
  quantity: number;
  room: {
    id: string;
    name: string;
  };
  status: [string, string];
}

export interface RequestOptionState {
  id: string;
  translation: string;
  category: string;
  status: true;
}

export interface RequestOptionTemplateState {
  id: string;
  created_by: string;
  updated_by: string | null;
  name: string;
  template: RequestOptionState[];
}

export interface PaginationProps {
  has_previous: boolean;
  has_next: boolean;
  current_page: number;
  total_pages: number;
  total_objects: number;
}

// Define a type for the slice state
export interface RequestsState {
  actionable: {
    data: ActionableRequestState[] | null;
    pagination: PaginationProps;
    loading: boolean;
    waiting: boolean;
    errors: string[];
    queue: ActionableRequestState[] | null;
  };
  request_options: {
    data: RequestOptionState[] | null;
    loading: boolean;
    waiting: boolean;
    errors: string[];
  };
  templates: {
    data: RequestOptionTemplateState[] | null;
    loading: boolean;
    waiting: boolean;
    errors: string[];
  };
  options: {
    [key: string]: any;
  };
}

// Define the initial state using that type
const initialState: RequestsState = {
  actionable: {
    data: null,
    pagination: {
      has_previous: false,
      has_next: false,
      current_page: 1,
      total_pages: 1,
      total_objects: 0,
    },
    loading: false,
    waiting: false,
    errors: [],
    queue: null,
  },
  request_options: {
    data: null,
    loading: false,
    waiting: false,
    errors: [],
  },
  templates: {
    data: null,
    loading: false,
    waiting: false,
    errors: [],
  },
  options: {},
};

//////////////////////////////
//////// Async Thunks ////////
//////////////////////////////

// Get actionable requests for authenticated user
export const getActionableRequests = createAsyncThunk(
  "requests/actionable/get",
  async (
    obj: {
      pageNum: number | boolean;
      category: string | null;
      dateRange: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/voice/requests/?page=${obj.pageNum}`;
      if (obj.category) url += `&category=${obj.category}`;
      if (obj.dateRange) url += `&dateRange=${obj.dateRange}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get actionable requests for authenticated user in the background
export const getActionableRequestsInBackground = createAsyncThunk(
  "requests/actionable/in-background/get",
  async (
    obj: {
      pageNum: number | boolean;
      category: string | null;
      dateRange: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/voice/requests/?page=${obj.pageNum}`;
      if (obj.category) url += `&category=${obj.category}`;
      if (obj.dateRange) url += `&dateRange=${obj.dateRange}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Update status of actionable requests
export const updateStatusRequests = createAsyncThunk(
  "requests/actionable/status/put",
  async (
    obj: {
      requestIds: string[];
      status: "Complete" | "In Progress" | "Deleted";
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = { id: obj.requestIds, status: obj.status };
      const res = await axios.put("/api/voice/requests/status/", body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get available request options for property
export const getRequestOptions = createAsyncThunk(
  "requests/options/get",
  async (args, { getState, rejectWithValue }) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/voice/request-options`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Update status of request options
export const updateStatusRequestOptions = createAsyncThunk(
  "requests/options/status/put",
  async (
    obj: {
      requestOptionsStatus: { [key: string]: boolean };
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = { status: obj.requestOptionsStatus };
      const res = await axios.put(
        "/api/voice/request-options/status/",
        body,
        config,
      );
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get available request option templates for property
export const getRequestOptionTemplates = createAsyncThunk(
  "requests/options/templates/get",
  async (args, { getState, rejectWithValue }) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/voice/request-options/template`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Create request option template
export const createRequestOptionTemplate = createAsyncThunk(
  "requests/options/templates/post",
  async (
    obj: {
      name: string;
      property: string;
      options: string[];
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        name: obj.name,
        property: obj.property,
        options: obj.options,
      };
      const res = await axios.post(
        "/api/voice/request-options/template/",
        body,
        config,
      );
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Update request option template
export const updateRequestOptionTemplate = createAsyncThunk(
  "requests/options/templates/put",
  async (
    obj: {
      templateId: string;
      name: string;
      property: string;
      options: string[];
      roomsApply: string[];
      roomsRemove: string[];
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        id: obj.templateId,
        name: obj.name,
        property: obj.property,
        options: obj.options,
        rooms_apply: obj.roomsApply,
        rooms_remove: obj.roomsRemove,
      };
      const res = await axios.put(
        "/api/voice/request-options/template/",
        body,
        config,
      );
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Delete request option template
export const deleteRequestOptionTemplate = createAsyncThunk(
  "requests/options/template/delete",
  async (
    obj: {
      templateId: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token and data
      const config = {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
        data: { id: obj.templateId },
      };

      const res = await axios.delete(
        "/api/voice/request-options/template",
        config,
      );
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

/////////////////////////////
//////// Redux slice ////////
/////////////////////////////

const requestsSlice = createSlice({
  name: "requests",
  initialState,
  reducers: {
    liveUpdateRequests: {
      prepare(requests: ActionableRequestState[]) {
        return { payload: { requests } };
      },
      reducer(
        state,
        action: PayloadAction<{ requests: ActionableRequestState[] }>,
      ) {
        const { requests } = action.payload;
        const newRequestIds = requests.map((request) => request.id);
        const existingRequests =
          state.actionable.data === null ? [] : state.actionable.data;

        // Loop through existingRequests and if id matches id in newRequestIds, replace the value with the matching object in requests
        let updatedRequests = existingRequests.map((existingRequest) => {
          if (newRequestIds.includes(existingRequest.id)) {
            const newRequest = requests.filter(
              (request) => request.id === existingRequest.id,
            )[0];
            return newRequest;
          } else {
            return existingRequest;
          }
        });
        updatedRequests = updatedRequests.filter(
          (x) => validRequestStatus.includes(x.status[0]),
        );
        state.actionable.data = updatedRequests;
      },
    },
    liveRemoveRequests: {
      prepare(requests: ActionableRequestState[]) {
        return { payload: { requests } };
      },
      reducer(
        state,
        action: PayloadAction<{ requests: ActionableRequestState[] }>,
      ) {
        const { requests } = action.payload;
        const removeRequestIds = requests.map((request) => request.id);

        // Remove requests from state
        const existingRequests =
          state.actionable.data === null ? [] : state.actionable.data;
        const updatedRequests = existingRequests.filter(
          (request) => !removeRequestIds.includes(request.id),
        );
        state.actionable.data = updatedRequests;
      },
    },
    liveAddToQueue: {
      prepare(request: ActionableRequestState) {
        return { payload: { request } };
      },
      reducer(
        state,
        action: PayloadAction<{ request: ActionableRequestState }>,
      ) {
        const existingQueue = state.actionable.queue === null ? [] : state.actionable.queue;

        // Check if new incoming request is in updated queue
        let check = false;
        let updatedQueue = existingQueue.map((r) => {
          if (r.id === action.payload.request.id) {
            check = true
            return action.payload.request;
          }
          return r;
        });

        // If incoming request was not in updated queue, add it
        if (check === false) {
          updatedQueue = [action.payload.request, ...updatedQueue];
        }

        // Filter out any requests with invalid status
        updatedQueue = updatedQueue.filter((x) => validRequestStatus.includes(x.status[0]))
        state.actionable.queue = updatedQueue;
      },
    },
    liveAddQueueToRequests: {
      prepare(requests: ActionableRequestState[]) {
        return { payload: { requests } };
      },
      reducer(
        state,
        action: PayloadAction<{ requests: ActionableRequestState[] }>,
      ) {

        // Get existing queue
        const existingQueue = state.actionable.queue === null ? [] : state.actionable.queue;

        // Get existing requests
        const existingRequests = state.actionable.data === null ? [] : state.actionable.data;

        // Get existing request IDs
        const existingRequestIds = existingRequests.map((r) => r.id);

        // Filter out any requests from the queue that are already in the existing requests
        const filteredQueue = existingQueue.filter((r) => !existingRequestIds.includes(r.id));

        // Update state as filtered queue and existing requests
        let finalRequests = [...filteredQueue, ...existingRequests];
        finalRequests = finalRequests.filter((x) => validRequestStatus.includes(x.status[0]))
        state.actionable.data = finalRequests;
        state.actionable.queue = null;
      },
    },
  },
  extraReducers: (builder) => {
    builder

      // Get actionable requests
      .addCase(getActionableRequests.pending, (state) => {
        state.actionable.loading = true;
        state.actionable.waiting = false;
      })
      .addCase(getActionableRequests.fulfilled, (state, action) => {
        state.actionable.data = action.payload.data.data;
        state.actionable.pagination = action.payload.data.pagination;
        state.options.requests = action.payload.data.options.requests;
        state.options.rooms_made_request =
          action.payload.data.options.rooms_made_request;
        state.actionable.loading = false;
        state.actionable.waiting = false;
      })
      .addCase(getActionableRequests.rejected, (state) => {
        state.actionable.loading = false;
        state.actionable.waiting = false;
      })

      // Get actionable requests in background
      .addCase(getActionableRequestsInBackground.pending, (state) => { })
      .addCase(getActionableRequestsInBackground.fulfilled, (state, action) => {
        state.actionable.data = action.payload.data.data;
        state.actionable.pagination = action.payload.data.pagination;
        state.options.requests = action.payload.data.options.requests;
        state.options.rooms_made_request =
          action.payload.data.options.rooms_made_request;
      })
      .addCase(getActionableRequestsInBackground.rejected, (state) => { })

      // Update status of actionable requests
      .addCase(updateStatusRequests.pending, (state) => {
        state.actionable.waiting = true;
      })
      .addCase(updateStatusRequests.fulfilled, (state, action) => {
        const updateType = action.meta.arg.status;
        const idsOfRequests = action.payload.data;
        const previousData =
          state.actionable.data === null ? [] : state.actionable.data;
        if (updateType === "Complete" || updateType === "Deleted") {
          const newData = previousData.filter(
            (request) => !idsOfRequests.includes(request.id),
          );
          state.actionable.data = newData;
        } else {
          const newData = [];
          for (let i = 0; i < previousData.length; i++) {
            const request = previousData[i];
            if (idsOfRequests.includes(request.id)) {
              request.status = ["I", updateType];
            }
            newData.push(request);
          }
          state.actionable.data = newData;
        }
        state.actionable.waiting = false;
      })
      .addCase(updateStatusRequests.rejected, (state) => {
        state.actionable.waiting = false;
      })

      // Get available request options for property
      .addCase(getRequestOptions.pending, (state) => {
        state.request_options.loading = true;
        state.request_options.waiting = false;
      })
      .addCase(getRequestOptions.fulfilled, (state, action) => {
        state.request_options.data = action.payload.data.data;
        state.options.category = action.payload.data.options.category;
        state.request_options.loading = false;
        state.request_options.waiting = false;
      })
      .addCase(getRequestOptions.rejected, (state) => {
        state.request_options.loading = false;
        state.request_options.waiting = false;
      })

      // Update status of request options
      .addCase(updateStatusRequestOptions.pending, (state) => {
        state.request_options.loading = true;
        state.request_options.waiting = true;
      })
      .addCase(updateStatusRequestOptions.fulfilled, (state, action) => {
        const previousData =
          state.request_options.data === null ? [] : state.request_options.data;
        const newData = [];
        for (let i = 0; i < previousData.length; i++) {
          const candidate = previousData[i];
          if (candidate.id === action.payload.data.id) {
            newData.push(action.payload.data);
          } else {
            newData.push(candidate);
          }
        }
        state.request_options.data = newData;
        state.request_options.loading = false;
        state.request_options.waiting = false;
      })
      .addCase(updateStatusRequestOptions.rejected, (state) => {
        state.request_options.loading = false;
        state.request_options.waiting = false;
      })

      // Get available request option templates for property
      .addCase(getRequestOptionTemplates.pending, (state) => {
        state.templates.loading = true;
        state.templates.waiting = false;
      })
      .addCase(getRequestOptionTemplates.fulfilled, (state, action) => {
        state.templates.data = action.payload.data.data;
        state.options.requests = action.payload.data.options.requests;
        state.templates.loading = false;
        state.templates.waiting = false;
      })
      .addCase(getRequestOptionTemplates.rejected, (state) => {
        state.templates.loading = false;
        state.templates.waiting = false;
      })

      // Create request option template
      .addCase(createRequestOptionTemplate.pending, (state) => {
        state.templates.loading = true;
        state.templates.waiting = true;
      })
      .addCase(createRequestOptionTemplate.fulfilled, (state, action) => {
        state.templates.data =
          state.templates.data === null
            ? [action.payload.data]
            : [...state.templates.data, action.payload.data];
        state.templates.loading = false;
        state.templates.waiting = false;
      })
      .addCase(createRequestOptionTemplate.rejected, (state) => {
        state.templates.loading = false;
        state.templates.waiting = false;
      })

      // Update request option template
      .addCase(updateRequestOptionTemplate.pending, (state) => {
        state.templates.loading = true;
        state.templates.waiting = true;
      })
      .addCase(updateRequestOptionTemplate.fulfilled, (state, action) => {
        const previousData =
          state.templates.data === null ? [] : state.templates.data;
        const newData = [];
        for (let i = 0; i < previousData.length; i++) {
          const template = previousData[i];
          if (template.id === action.payload.data.id) {
            newData.push(action.payload.data);
          } else {
            newData.push(template);
          }
        }
        state.templates.data = newData;
        state.templates.loading = false;
        state.templates.waiting = false;
      })
      .addCase(updateRequestOptionTemplate.rejected, (state) => {
        state.templates.loading = false;
        state.templates.waiting = false;
      })

      // Delete request option template
      .addCase(deleteRequestOptionTemplate.pending, (state) => {
        state.templates.loading = true;
        state.templates.waiting = true;
      })
      .addCase(deleteRequestOptionTemplate.fulfilled, (state, action) => {
        const updatedData =
          state.templates.data === null
            ? []
            : state.templates.data.filter(
              (template) => template.id !== action.payload.data.id,
            );
        state.templates.data = updatedData;
        state.templates.loading = false;
        state.templates.waiting = false;
      })
      .addCase(deleteRequestOptionTemplate.rejected, (state) => {
        state.templates.loading = false;
        state.templates.waiting = false;
      });
  },
});

export const {
  liveUpdateRequests,
  liveRemoveRequests,
  liveAddToQueue,
  liveAddQueueToRequests,
} = requestsSlice.actions;
export default requestsSlice.reducer;
